/**
 * Phase 3F: Admin Governance Panel
 * Manages feature flags and client opt-ins
 */

import React from 'react';
import { Settings, ToggleLeft, ToggleRight, Building2, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import type { FeatureFlag, ClientOptIn, Phase3FeatureFlag } from '@/lib/governance/types';

interface Phase3AdminPanelProps {
  featureFlags: FeatureFlag[];
  clientOptIn: ClientOptIn | null;
  onToggleFeatureFlag: (flagName: Phase3FeatureFlag, enabled: boolean) => void;
  onToggleClientOptIn: (optedIn: boolean) => void;
  isLoading?: boolean;
  clientName?: string;
}

const FEATURE_FLAG_LABELS: Record<Phase3FeatureFlag, { label: string; description: string }> = {
  phase_3_enabled: {
    label: 'Phase 3 Master Toggle',
    description: 'Enable or disable all Phase 3 analytics features globally',
  },
  pattern_analysis_enabled: {
    label: 'Historical Pattern Analysis',
    description: 'Retrospective authorization pattern detection (Phase 3B)',
  },
  revenue_flags_enabled: {
    label: 'Revenue Leakage Flags',
    description: 'Aggregate revenue integrity observations (Phase 3C)',
  },
  subset_insights_enabled: {
    label: 'Subset Refinement Insights',
    description: 'CPT subset usage observations for surgeons (Phase 3D)',
  },
  financial_variance_enabled: {
    label: 'Financial Variance Reporting',
    description: 'Hospital-level variance reports (Phase 3E)',
  },
};

export function Phase3AdminPanel({
  featureFlags,
  clientOptIn,
  onToggleFeatureFlag,
  onToggleClientOptIn,
  isLoading = false,
  clientName = 'Current Hospital',
}: Phase3AdminPanelProps) {
  const masterEnabled = featureFlags.find(f => f.flagName === 'phase_3_enabled')?.isEnabled ?? false;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Phase 3 Governance Controls
        </CardTitle>
        <CardDescription>
          Manage feature flags, opt-ins, and access controls for Phase 3 analytics
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Feature Flags Section */}
        <div>
          <h4 className="text-sm font-medium mb-4 flex items-center gap-2">
            <ToggleLeft className="h-4 w-4" />
            Feature Flags
          </h4>
          <div className="space-y-4">
            {featureFlags.map((flag) => {
              const config = FEATURE_FLAG_LABELS[flag.flagName];
              const isMasterFlag = flag.flagName === 'phase_3_enabled';
              const isDisabled = !isMasterFlag && !masterEnabled;

              return (
                <div
                  key={flag.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isMasterFlag ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
                  } ${isDisabled ? 'opacity-50' : ''}`}
                >
                  <div className="space-y-0.5">
                    <Label className="font-medium flex items-center gap-2">
                      {config.label}
                      {isMasterFlag && (
                        <Badge variant={flag.isEnabled ? 'default' : 'secondary'} className="text-xs">
                          {flag.isEnabled ? 'ON' : 'OFF'}
                        </Badge>
                      )}
                    </Label>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  </div>
                  <Switch
                    checked={flag.isEnabled}
                    onCheckedChange={(checked) => onToggleFeatureFlag(flag.flagName, checked)}
                    disabled={isDisabled}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Client Opt-In Section */}
        <div>
          <h4 className="text-sm font-medium mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Hospital Opt-In
          </h4>
          <div className={`flex items-center justify-between p-4 rounded-lg border ${
            !masterEnabled ? 'opacity-50' : ''
          }`}>
            <div className="space-y-1">
              <Label className="font-medium">{clientName}</Label>
              <p className="text-xs text-muted-foreground">
                {clientOptIn?.optedIn
                  ? `Opted in on ${new Date(clientOptIn.optedInAt || '').toLocaleDateString()}`
                  : 'Not opted in to Phase 3 analytics'
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={clientOptIn?.optedIn ? 'default' : 'outline'}>
                {clientOptIn?.optedIn ? 'Enabled' : 'Disabled'}
              </Badge>
              <Switch
                checked={clientOptIn?.optedIn ?? false}
                onCheckedChange={onToggleClientOptIn}
                disabled={!masterEnabled}
              />
            </div>
          </div>
          {!masterEnabled && (
            <p className="text-xs text-muted-foreground mt-2">
              Enable the master toggle to manage hospital opt-ins
            </p>
          )}
        </div>

        {/* Info Notice */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-4">
          <div className="flex items-start gap-2">
            <Users className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Surgeon Opt-In Required</p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Even with hospital opt-in enabled, individual surgeons must opt in separately 
                and accept the disclaimer before seeing Phase 3 insights.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
